import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
} from "@mui/material";
import { useSearchParams, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { openSnackAlert } from "../../helper";
import useUserStore from "../../zustand/useUserStore";
import AppLoader from "../../components/appLoader";
import CustomButton from "../../components/customButton";
import CustomSelect from "../../components/customSelect";
import CustomTimePicker from "../../components/customTimePicker";
import CustomDatePicker from "../../components/datePicker";
import DynamicTable from "../../components/dynamicTable";
import TextInput from "../../components/textInput";
import { CircleUser, History } from "lucide-react";
import {
  getWorkersAttendanceByDate,
  adjustAttendanceRecord,
  addAttendanceRecord,
  deleteAttendanceRecord,
} from "../../services/Attendance";
import { getAllWorkers } from "../../services/Workers";

const ADJUST_FILTERS_KEY = "attendance_adjust_filters";

/**
 * Attendance Adjustment Page
 * URL: /attendance-adjust (optional query params: ?employeeId=X&date=Y)
 */
const AttendanceAdjust = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useUserStore();

  // Read optional query params: ?employeeId=X&date=Y
  const urlEmployeeId = searchParams.get("employeeId") || "";
  const urlDate = searchParams.get("date") || "";

  const getSavedFilters = () => {
    try {
      return JSON.parse(sessionStorage.getItem(ADJUST_FILTERS_KEY) || "{}");
    } catch {
      return {};
    }
  };

  // State
  const [workers, setWorkers] = useState([]);
  const [selectedWorker, setSelectedWorker] = useState(() => getSavedFilters().workerId || "");
  const [selectedDate, setSelectedDate] = useState(() => getSavedFilters().date || "");
  const [attendanceData, setAttendanceData] = useState([]);
  const [loading, setLoading] = useState(true);

  // Adjust dialog state
  const [adjustDialogOpen, setAdjustDialogOpen] = useState(false);
  const [adjustRecord, setAdjustRecord] = useState(null);
  const [adjustTime, setAdjustTime] = useState("");
  const [adjustNotes, setAdjustNotes] = useState("");

  // Add record dialog state
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addType, setAddType] = useState("checkIn");
  const [addTime, setAddTime] = useState("");

  // Handle type change — accepts MUI event OR direct value
  const handleAddTypeChange = (event) => {
    if (event && event.target) {
      setAddType(event.target.value);
    } else if (typeof event === "string") {
      setAddType(event);
    } else {
      setAddType("checkIn");
    }
  };

  // Load workers on mount
  useEffect(() => {
    let cancelled = false;
    const loadWorkers = async () => {
      try {
        const response = await getAllWorkers();
        if (!cancelled && (response?.status === 200 || response?.status === 201)) {
          const workersList = response?.data?.data?.workers || response?.data?.workers || [];
          setWorkers(workersList);
        }
      } catch (error) {
        if (!cancelled) openSnackAlert("Error loading workers", "error");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadWorkers();
    return () => {
      cancelled = true;
    };
  }, []);

  // URL params take priority; otherwise keep persisted worker/date selection
  useEffect(() => {
    if (!workers.length) return;

    if (urlEmployeeId) {
      const worker = workers.find((w) => w.employeeId === urlEmployeeId);
      if (worker) {
        setSelectedWorker(worker._id);
        if (urlDate) setSelectedDate(urlDate);
      }
      return;
    }

    const saved = getSavedFilters();
    if (saved.workerId && workers.some((w) => w._id === saved.workerId)) {
      setSelectedWorker(saved.workerId);
    }
    if (saved.date) {
      setSelectedDate(saved.date);
    }
  }, [workers, urlEmployeeId, urlDate]);

  // Persist selection so it survives navigation (e.g. history page → back)
  useEffect(() => {
    sessionStorage.setItem(
      ADJUST_FILTERS_KEY,
      JSON.stringify({
        workerId: selectedWorker || "",
        date: selectedDate || "",
      })
    );
  }, [selectedWorker, selectedDate]);

  // Load attendance data when selectedWorker and selectedDate change
  useEffect(() => {
    if (selectedWorker && selectedDate) {
      loadAttendanceData(selectedWorker, selectedDate);
    } else {
      setAttendanceData([]);
    }
  }, [selectedWorker, selectedDate]);

  // Load attendance data
  const loadAttendanceData = async (workerId, date) => {
    setLoading(true);
    try {
      const response = await getWorkersAttendanceByDate(date, workerId);
      if (response?.status === 200 || response?.status === 201) {
        setAttendanceData(response?.data?.records || response?.data?.data?.records || []);
      }
    } catch (error) {
      openSnackAlert(error?.message || "Something went wrong", "error");
    } finally {
      setLoading(false);
    }
  };

  // Handle worker change — accepts MUI event OR direct value
  const handleWorkerChange = (event) => {
    if (event && event.target) {
      setSelectedWorker(event.target.value);
    } else if (typeof event === "string") {
      setSelectedWorker(event);
    } else {
      setSelectedWorker("");
    }
  };

  // Handle date change — accepts dayjs object, Date object, or string
  const handleDateChange = (value) => {
    if (value == null) {
      setSelectedDate("");
    } else if (typeof value === "string") {
      setSelectedDate(value);
    } else if (value.format) {
      setSelectedDate(value.format("YYYY-MM-DD"));
    } else if (value instanceof Date) {
      setSelectedDate(format(value, "yyyy-MM-dd"));
    } else {
      setSelectedDate("");
    }
  };

  // Open adjust dialog — pre-fill time and notes from existing record
  const openAdjustDialog = (record) => {
    setAdjustRecord(record);
    setAdjustTime(record.time || "");
    setAdjustNotes(record.notes || "");
    setAdjustDialogOpen(true);
  };

  // Loader for adjust/add/delete operations
  const [saving, setSaving] = useState(false);

  // Save adjustments — time and notes
  const handleAdjustSave = async () => {
    if (!adjustRecord) return;
    setSaving(true);
    try {
      const response = await adjustAttendanceRecord(adjustRecord._id, {
        time: adjustTime,
        date: selectedDate,
        notes: adjustNotes,
      });
      if (response?.status === 200 || response?.status === 201) {
        openSnackAlert("Attendance record adjusted successfully!", "success");
        setAdjustDialogOpen(false);
        loadAttendanceData(selectedWorker, selectedDate);
      }
    } catch (error) {
      openSnackAlert(error?.message || "Something went wrong", "error");
    } finally {
      setSaving(false);
    }
  };

  // Add new record
  const handleAddRecord = async () => {
    setSaving(true);
    try {
      const response = await addAttendanceRecord({
        workerId: selectedWorker,
        type: addType,
        time: addTime,
        date: selectedDate,
      });
      if (response?.status === 200 || response?.status === 201) {
        openSnackAlert("Record added successfully!", "success");
        setAddDialogOpen(false);
        loadAttendanceData(selectedWorker, selectedDate);
      }
    } catch (error) {
      openSnackAlert(error?.message || "Something went wrong", "error");
    } finally {
      setSaving(false);
    }
  };

  // Delete record
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this record?")) return;
    setSaving(true);
    try {
      const response = await deleteAttendanceRecord(id);
      if (response?.status === 200 || response?.status === 201) {
        openSnackAlert("Record deleted successfully!", "success");
        loadAttendanceData(selectedWorker, selectedDate);
      }
    } catch (error) {
      openSnackAlert(error?.message || "Something went wrong", "error");
    } finally {
      setSaving(false);
    }
  };

  // CSV Export
  const exportToCSV = () => {
    if (!attendanceData.length) {
      openSnackAlert("No data available to export", "warning");
      return;
    }

    const headers = ["Date", "Type", "Time", "Status", "Adjusted", "Adjusted At", "Notes"];

    const csvData = attendanceData.map((record) => {
      return [
        record.date || "-",
        record.type || "-",
        record.time || "-",
        record.status || "-",
        record.isAdjusted ? "Yes" : "No",
        record.adjustedAt ? format(new Date(record.adjustedAt), "dd/MM/yyyy HH:mm") : "-",
        record.notes || "-",
      ];
    });

    const csvContent = [
      `Employee: ${selectedWorker || "All"}`,
      `Date: ${selectedDate || "All"}`,
      `Generated: ${format(new Date(), "dd/MM/yyyy HH:mm")}`,
      "",
      headers.join(","),
      ...csvData.map((row) => row.map(escapeCsvCell).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `attendance_${selectedWorker || "all"}_${selectedDate || "all"}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    openSnackAlert("Attendance data exported successfully!", "success");
  };

  const escapeCsvCell = (val) => {
    const str = val == null ? "" : String(val);
    if (/[",\r\n]/.test(str)) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  // Get selected worker details
  const getSelectedWorkerDetails = () => {
    if (!selectedWorker || !workers.length) return null;
    return workers.find((w) => w._id === selectedWorker);
  };

  const workerDetails = getSelectedWorkerDetails();

  // --- Dynamic Table config ---
  const tableHeader = [
    { id: "date", title: "Date", align: "left" },
    { id: "type", title: "Type", align: "left" },
    { id: "time", title: "Time", align: "left" },
    // { id: "adjusted", title: "Adjusted", align: "center" },
    { id: "adjustedAt", title: "Adjusted At", align: "center" },
    { id: "notes", title: "Notes", align: "left" },
    { id: "actions", title: "Actions", align: "center" },
  ];

  const displayRows = ["date", "type", "time", "adjustedAt", "notes", "actions"];

  const tableData = attendanceData.map((record) => ({
    ...record,
    date: record.date ? format(new Date(record.date), "dd/MM/yyyy") : "-",
    adjusted: record.isAdjusted ? "Yes" : "No",
    adjustedAt: record.adjustedAt ? format(new Date(record.adjustedAt), "dd/MM/yyyy HH:mm") : "-",
    notes: record.notes || "-",
  }));

  return (
    <Box bgcolor="primary.bgLight" padding={"20px"} borderRadius={"22px"} mt={0}>
      <AppLoader isVisible={loading || saving} />
      <Grid container spacing={3}>
        {/* Header */}
        <Grid item xs={12}>
          <Typography variant="h4">Attendance Adjustment</Typography>
          <Typography variant="description">
            Adjust worker attendance records — time and notes
          </Typography>
        </Grid>

        {/* Filters */}
        <Grid item xs={12} sm={6} md={4}>
          <Typography variant="body1" gutterBottom>
            Select Worker
          </Typography>
          <CustomSelect
            name="worker"
            placeholder="Select Worker"
            value={selectedWorker}
            onChange={handleWorkerChange}
            icon={<CircleUser size={16} />}
            sx={{ width: "100%", height: "36px" }}
          >
            {workers?.map((worker) => (
              <MenuItem key={worker._id} value={worker._id}>
                {worker.firstName} {worker.lastName} ({worker.employeeId})
              </MenuItem>
            ))}
          </CustomSelect>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Typography variant="body1" gutterBottom>
            Select Date
          </Typography>
          <CustomDatePicker
            value={selectedDate}
            onChange={(val) => handleDateChange(val)}
          />
        </Grid>

        {/* Worker info */}
        {workerDetails && (
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary">
              {workerDetails.firstName} {workerDetails.lastName} — {workerDetails.employeeId}
            </Typography>
          </Grid>
        )}

        {/* Action buttons — History is always available */}
        <Grid item xs={12}>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <CustomButton
              btnLabel="Adjustment History"
              handlePressBtn={() => navigate("/adjustment-history")}
               btnBgColor="#2E7D32"
                  btnTextColor="#fff"
                  borderRadius="8px"
                  sx={{ px: 3, py: 1 }}
              startIcon={<History size={16} />}
            />
            {selectedWorker && selectedDate && (
              <>
                {/* <CustomButton
                  btnLabel="Export CSV"
                  handlePressBtn={exportToCSV}
                  btnBgColor="#2E7D32"
                  btnTextColor="#fff"
                  borderRadius="8px"
                  sx={{ px: 3, py: 1 }}
                /> */}
                <CustomButton
                  btnLabel="Add Record"
                  handlePressBtn={() => setAddDialogOpen(true)}
                  btnBgColor="#fff"
                  btnTextColor="#225B27"
                  borderColor="#225B27"
                  isBorder="1px solid #225B27"
                  sx={{ boxShadow: 0, borderRadius: "4px" }}
                />
              </>
            )}
          </Stack>
        </Grid>

        {/* Attendance Table */}
        <Grid item xs={12}>
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "200px",
              }}
            >
              <Typography>Loading attendance data...</Typography>
            </Box>
          ) : attendanceData.length > 0 ? (
            <DynamicTable
              tableHeader={tableHeader}
              tableData={tableData}
              displayRows={displayRows}
              showPagination={false}
              onEdit={openAdjustDialog}
              onDelete={handleDelete}
            />
          ) : (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "200px",
                color: "text.secondary",
              }}
            >
              <Typography>No attendance data found for this date</Typography>
            </Box>
          )}
        </Grid>
      </Grid>

      {/* Adjust Dialog — Time and Notes */}
      <Dialog
        open={adjustDialogOpen}
        onClose={() => setAdjustDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Adjust Attendance Record</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <CustomTimePicker
                value={adjustTime}
                onChange={setAdjustTime}
                placeholder="Select time"
              />
            </Grid>
            <Grid item xs={12}>
              <TextInput
                label="Notes"
                name="notes"
                value={adjustNotes}
                onChange={(e) => setAdjustNotes(e.target.value)}
                placeholder="Add notes (optional)"
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <CustomButton btnLabel="Cancel" handlePressBtn={() => setAdjustDialogOpen(false)} />
          <CustomButton
            btnLabel={saving ? "Saving..." : "Save Changes"}
            handlePressBtn={handleAdjustSave}
            btnBgColor="#225B27"
            btnTextColor="#fff"
            disabled={saving}
          />
        </DialogActions>
      </Dialog>

      {/* Add Record Dialog — Time + Type */}
      <Dialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Attendance Record</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <CustomTimePicker
                value={addTime}
                onChange={setAddTime}
                placeholder="Select time"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <CustomSelect
                placeholder="Type"
                value={addType}
                onChange={handleAddTypeChange}
                sx={{ width: "100%", height: "40px" }}
              >
                <MenuItem value="checkIn">Check In</MenuItem>
                <MenuItem value="breakIn">Break In</MenuItem>
                <MenuItem value="breakOut">Break Out</MenuItem>
                <MenuItem value="checkOut">Check Out</MenuItem>
              </CustomSelect>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <CustomButton btnLabel="Cancel" handlePressBtn={() => setAddDialogOpen(false)} />
          <CustomButton
            btnLabel={saving ? "Adding..." : "Add Record"}
            handlePressBtn={handleAddRecord}
            btnBgColor="#066112"
            btnTextColor="#fff"
            disabled={saving}
          />
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AttendanceAdjust;
